import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import ReactEcharts from "echarts-for-react";
import * as echarts from "echarts";
import chroma from "chroma-js";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Grid,
  TextField,
  InputAdornment,
  Typography,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import { makeStyles } from "@material-ui/core/styles";
import ChartStyle from "styles/ChartStyle";

const useStyles = makeStyles((theme) => ({
  card: {
    borderRadius: 8,
    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
    border: "1px solid rgba(18, 24, 38, 0.06)",
  },
  header: {
    marginBottom: theme.spacing(1.5),
  },
  search: {
    width: 280,
    maxWidth: "100%",
  },
  countField: {
    width: 100,
    maxWidth: "100%",
    marginRight: theme.spacing(1),
  },
  title: {
    fontSize: 16,
    fontWeight: 600,
    lineHeight: 1.35,
  },
  subtitle: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    lineHeight: 1.5,
  },
  chartBox: {
    width: "100%",
    height: 560,
  },
  loadingWrap: {
    minHeight: 560,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  emptyWrap: {
    minHeight: 560,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.palette.text.secondary,
  },
}));

const formatDateLabel = (value) => {
  if (!value) return "";
  const parts = String(value).split("-");
  if (parts.length !== 3) return String(value);
  return `${Number(parts[1])}/${Number(parts[2])}`;
};

const hashColor = (label) => {
  const text = String(label || "");
  const hash = Array.from(text).reduce(
    (acc, char) => (acc * 31 + char.charCodeAt(0)) >>> 0,
    0
  );
  return chroma.hsl(hash % 360, 0.7, 0.56).hex();
};

const PicTreeByAreaTrendChart = ({
  title,
  subtitle,
  data,
  loading,
}) => {
  const classes = useStyles();
  const chartRef = useRef(null);
  const [legendQuery, setLegendQuery] = useState("");
  const [displayCount, setDisplayCount] = useState("20");

  useEffect(() => {
    echarts.registerTheme("PicTreeByAreaTrendTheme", ChartStyle);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      chartRef.current && chartRef.current.getEchartsInstance().resize();
    }, 100);

    return () => clearTimeout(timer);
  }, [data]);

  const allSeries = useMemo(
    () => (Array.isArray(data?.series) ? data.series : []),
    [data?.series]
  );
  const normalizedQuery = legendQuery.trim().toLowerCase();
  const visibleCount = useMemo(() => {
    const parsed = Number.parseInt(displayCount, 10);
    if (Number.isNaN(parsed) || parsed <= 0) {
      return 10;
    }
    return parsed;
  }, [displayCount]);
  const legendData = useMemo(() => {
    const seriesNames = allSeries.map((series) => series.name);
    const filteredNames = normalizedQuery
      ? seriesNames.filter((name) =>
          String(name).toLowerCase().includes(normalizedQuery)
        )
      : seriesNames;

    return filteredNames.slice(0, visibleCount);
  }, [allSeries, normalizedQuery, visibleCount]);
  const visibleSeries = useMemo(() => {
    const visibleSet = new Set(legendData);
    return allSeries.filter((series) => visibleSet.has(series.name));
  }, [allSeries, legendData]);

  const option = useMemo(
    () => ({
      tooltip: {
        trigger: "axis",
        axisPointer: {
          type: "line",
        },
        valueFormatter: (value) => `${Number(value || 0).toFixed(2)}%`,
      },
      grid: {
        left: 48,
        right: 24,
        top: 96,
        bottom: 36,
        containLabel: true,
      },
      legend: {
        type: "scroll",
        top: 40,
        left: 48,
        right: 24,
        data: legendData,
        icon: "circle",
        itemWidth: 12,
        itemHeight: 12,
        textStyle: {
          color: "#475467",
          fontSize: 12,
        },
        pageIconColor: "#667085",
        pageIconInactiveColor: "#D0D5DD",
        pageTextStyle: {
          color: "#475467",
        },
      },
      xAxis: {
        type: "category",
        boundaryGap: false,
        data: Array.isArray(data?.date) ? data.date.map(formatDateLabel) : [],
        axisTick: {
          alignWithLabel: true,
        },
        axisLabel: {
          color: "#475467",
        },
      },
      yAxis: {
        type: "value",
        min: 0,
        max: 100,
        axisLabel: {
          color: "#475467",
          formatter: "{value}%",
        },
        splitLine: {
          lineStyle: {
            color: "rgba(148, 163, 184, 0.18)",
          },
        },
      },
      series: visibleSeries.map((series) => {
        const color = series.color || hashColor(series.name);
        return {
          name: series.name,
          type: "line",
          smooth: false,
          showSymbol: false,
          connectNulls: true,
          data: series.data || [],
          lineStyle: {
            width: 2,
            color,
          },
          itemStyle: {
            color,
          },
          emphasis: {
            focus: "series",
          },
        };
      }),
    }),
    [data, visibleSeries, legendData]
  );

  const hasData =
    Array.isArray(visibleSeries) &&
    visibleSeries.length > 0 &&
    Array.isArray(data?.date) &&
    data.date.length > 0;

  return (
    <Card className={classes.card}>
      <CardContent>
        <Grid
          container
          spacing={1}
          justifyContent="space-between"
          alignItems="flex-start"
          className={classes.header}
        >
          <Grid item xs={12} md={7}>
            <Typography className={classes.title}>{title}</Typography>
            {subtitle ? (
              <Typography className={classes.subtitle}>{subtitle}</Typography>
            ) : null}
          </Grid>
          <Grid item xs={6} sm="auto">
            <TextField
              className={classes.countField}
              variant="outlined"
              size="small"
              label="表示件数"
              type="number"
              value={displayCount}
              onChange={(event) => setDisplayCount(event.target.value)}
              inputProps={{
                min: 1,
                step: 1,
              }}
            />
            <TextField
              className={classes.search}
              variant="outlined"
              size="small"
              placeholder="検索"
              value={legendQuery}
              onChange={(event) => setLegendQuery(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>

        {loading ? (
          <Box className={classes.loadingWrap}>
            <CircularProgress size={36} />
          </Box>
        ) : hasData ? (
          <div className={classes.chartBox}>
            <ReactEcharts
              ref={chartRef}
              option={option}
              theme="PicTreeByAreaTrendTheme"
              notMerge
              lazyUpdate
              style={{ width: "100%", height: "100%" }}
            />
          </div>
        ) : (
          <Box className={classes.emptyWrap}>
            <Typography variant="body2">
              データがありません。
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

PicTreeByAreaTrendChart.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  data: PropTypes.shape({
    date: PropTypes.array,
    series: PropTypes.array,
    topLegendNames: PropTypes.array,
  }),
  loading: PropTypes.bool,
};

export default PicTreeByAreaTrendChart;
